import * as vp from 'vopay';

import { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import DashboardNavTop from '@/components/UI/DashboardNavTop';

import NavLeftContainer from '@/components/UI/NavLeftContainer/NavLeftContainer';

import DashboardRoutes from '@/components/Homepage/Routes';
import UsersRoutes from '@/components/Users/Route';
import AccountsContainer from '@/components/Accounts/AccountsContainer';
import BillingPackageRoutes from '@/components/BillingPackages/Routes';
import ReportRoutes from '@/components/Reports/Routes';
import AccountBillingRoutes from '@/components/AccountBilling/Routes';
import LeadsRoutes from '@/components/Leads/Routes';
import PartnersRoutes from '@/components/Partners/Routes';
import SupportRoutes from '@/components/Support/Routes';
import SettingsRoutes from '@/components/Settings/Routes';
import OnboardingFormTrackerRoutes from '@/components/OnBoardingFormTracker/Routes';
import OnboardingTrackerRoutes from '@/components/OnboardingTracker/Routes';

import TransactionsRoutes from '@/components/Transactions/Routes';
import EntitiesRoutes from '@/components/Entities/Routes';
import { useBillingPackages } from '@/providers/BillingPackages.provider';
import { useBranding } from '@/providers/Branding.provider';
import { useHubSpotDropdownDataSet } from '../providers/HubSpotDropdownData.provider';
import useActiveModule from '@/hooks/useActiveModule';
import styles from './Footer.module.scss';
import navBarStyles from './NavBar.module.scss';
import useFetchAccountBranding from '@/hooks/useApi/useFetchAccountBranding';
import { usePartnerData } from '@/providers/PartnerData.provider';
import useHubspotElement from '@/hooks/useHubspotElement';

const { Container, DashboardLayout } = vp;

export default function Dashboard() {
  const { t } = useTranslation('translations', { keyPrefix: 'Dashboard' });
  const { pathname } = useLocation();
  const { activeModule } = useActiveModule();
  const { refreshBillingPackages } = useBillingPackages();
  const { refreshHubSpotDropdownData } = useHubSpotDropdownDataSet();
  const { fetchAccountBranding } = useFetchAccountBranding();
  const { logo, smallLogo, setBrandingValues } = useBranding();
  const [expandMenu, setExpandMenu] = useState(false);
  const [brandingLoaded, setBrandingLoaded] = useState(false);
  const { response } = usePartnerData();
  const { navRef } = useHubspotElement();

  useEffect(() => {
    refreshBillingPackages({ useCached: true });
    refreshHubSpotDropdownData({ useCached: true });
  }, []);

  useEffect(() => {
    vp.loadScriptByURL('hs-script-loader', '//js.hs-scripts.com/6101456.js')
      .then(() => {
        console.log('HubSpot script loaded');
      })
      .catch((error) => {
        console.error('Failed to load HubSpot script:', error);
      });
  }, []);

  useEffect(() => {
    initBranding();
  }, [response.accountId]);

  async function initBranding() {
    setBrandingLoaded(false);
    const res = await fetchAccountBranding();

    if (!res.Success) {
      console.warn(res.ErrorMessage);
    }

    setBrandingValues({
      primaryColor: res.BrandingDetails.PrimaryColor,
      secondaryColor: res.BrandingDetails.SecondaryColor,
      logo: res.Logo,
      smallLogo: res.SmallLogo,
      appTitle: res.AppTitle,
      appFavicon: res.Favicon,
    });

    setBrandingLoaded(true);
  }

  const handlers = {
    onMouseEnter: (show: boolean) => {
      setExpandMenu(show);
    },
  };

  const isEmbedded = useMemo(() => pathname.split('/')[1] === 'onboarding-form-tracker', [pathname]);
  // The redesigned Accounts pages share the Accounts module (and its menu highlight) but not its routes.
  const isEntities = pathname.split('/')[1] === 'entities';

  if (!brandingLoaded) return null;

  return (
    <DashboardLayout
      handlers={handlers}
      pathname={pathname}
      isEmbedded={isEmbedded}
      expandMenu={expandMenu}
      navSubTop={
        <div className={navBarStyles.navbarContainer} ref={navRef}>
          <DashboardNavTop />
        </div>
      }
      navLeft={
        <NavLeftContainer
          logo={logo === '' ? undefined : logo}
          smallLogo={smallLogo === '' ? undefined : smallLogo}
          expandMenu={expandMenu}
        />
      }
      content={
        <Container maxWidth="lg" className={`${styles.mainContent} ${isEmbedded && styles.embedded}`}>
          {activeModule === 'dashboard' && <DashboardRoutes />}
          {activeModule === 'partners' && <PartnersRoutes />}
          {activeModule === 'users' && <UsersRoutes />}
          {activeModule === 'accounts' && !isEntities && <AccountsContainer />}
          {activeModule === 'accounts' && isEntities && <EntitiesRoutes />}
          {activeModule === 'leads' && <LeadsRoutes />}
          {activeModule === 'billingPackages' && <BillingPackageRoutes />}
          {activeModule === 'reports' && <ReportRoutes />}
          {activeModule === 'accountBilling' && <AccountBillingRoutes />}
          {activeModule === 'support' && <SupportRoutes />}
          {activeModule === 'transactions' && <TransactionsRoutes />}
          {activeModule === 'settings' && <SettingsRoutes />}
          {activeModule === 'onBoardingFormTracker' && <OnboardingFormTrackerRoutes />}
          {/* The new tracker, at a URL nothing links to until it replaces the old one (ID-14039). */}
          {activeModule === 'onBoardingFormTracker' && <OnboardingTrackerRoutes />}
        </Container>
      }
      footer={
        window.location.hostname.includes('vopay') || window.location.hostname.includes('localhost') ? (
          <div className={styles.footerContainer}>
            <div className={styles.container}>
              <p>
                ©VoPay International Inc. {new Date().getFullYear()}{' '}
                {t('Footer.allRightsReserved', { defaultValue: 'All Rights Reserved' })} |
                <a href="https://vopay.com/legal/services/tos/">
                  {' '}
                  {t('Footer.termsOfUse', { defaultValue: 'Terms of Use' })}
                </a>{' '}
                |
                <a href="https://vopay.com/legal/services/privacy-policy/">
                  {' '}
                  {t('Footer.privacyPolicy', { defaultValue: 'Privacy Policy' })}
                </a>
              </p>
            </div>
          </div>
        ) : (
          <></>
        )
      }
    />
  );
}
